import { useTranslation } from 'react-i18next';
import { TrendingUp, Phone } from 'lucide-react';
import { formatNumber, formatPercent } from '@/i18n/locale';
import type { DialerCounters } from '@/api/endpoints/analytics';

interface CallerStatsProps {
  totals: DialerCounters | null;
}

/**
 * Ce que les tuiles d'en-tete ne disent pas encore : le rendement par appel
 * ABOUTI, et les appels qui se coupent trop tot.
 *
 * Depuis W4, les deux chiffres viennent du serveur (totaux de la periode,
 * POST /api/analytics/dialer/performance) : connecte = l'appel a atteint
 * l'agent, conversion = RDV / connectes (CEO-7), appel court = connecte de
 * moins de 30 s. Sans appel connecte, « — » plutot qu'un 0 % trompeur.
 */
export default function CallerStats({ totals }: CallerStatsProps) {
    const { t } = useTranslation(['pages']);
    const connected = totals?.connected ?? 0;
    const conversionRate = connected > 0 ? ((totals?.meetings ?? 0) / connected) * 100 : null;
    const shortCalls = totals?.shortConnected ?? 0;
    const shortCallsRate = connected > 0 ? (shortCalls / connected) * 100 : null;

    return (
        <div className="bg-card rounded-xl border border-border p-5 space-y-5 min-w-0">
            <div className="min-w-0">
                <div className="flex items-center gap-2 mb-3 min-w-0">
                    <TrendingUp className="w-4 h-4 text-green-600 shrink-0" />
                    <h3 className="text-sm font-semibold text-foreground truncate">{t('pages:analytics.callerStats.efficiency.title')}</h3>
                </div>
                <p className="text-xs text-muted-foreground mb-1 break-words">{t('pages:analytics.callerStats.efficiency.conversionRate')}</p>
                <p className="text-2xl font-semibold text-foreground font-data truncate">{conversionRate === null ? '—' : formatPercent(conversionRate)}</p>
                <div className="h-1.5 bg-muted rounded-full mt-2 overflow-hidden">
                    <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${Math.min(conversionRate ?? 0, 100)}%`, background: 'var(--vf-accent)' }}
                    />
                </div>
            </div>

            <div className="border-t border-border pt-5 min-w-0">
                <div className="flex items-center gap-2 mb-3 min-w-0">
                    <Phone className="w-4 h-4 text-orange-600 shrink-0" />
                    <h3 className="text-sm font-semibold text-foreground truncate">{t('pages:analytics.callerStats.shortCalls.title')}</h3>
                </div>
                <div className="flex items-end justify-between gap-3 min-w-0">
                    <div className="min-w-0">
                        <p className="text-2xl font-semibold text-foreground font-data truncate">{formatNumber(shortCalls)}</p>
                        <p className="text-xs text-muted-foreground mt-1 truncate">{t('pages:analytics.callerStats.shortCalls.sub')}</p>
                    </div>
                    <div className="text-right min-w-0">
                        <p className="text-lg font-semibold text-orange-600 font-data truncate">{shortCallsRate === null ? '—' : formatPercent(shortCallsRate, 0)}</p>
                        <p className="text-xs text-muted-foreground truncate">{t('pages:analytics.callerStats.shortCalls.ofTotal')}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
